import { z } from "zod"

import { FUTURE_ACCOUNTING_PERIOD_ERROR, INVOICE_AMOUNT_TOLERANCE, INVOICE_TOTAL_MAX } from "../constants"
import type { InvoiceFormDialogMode } from "../types/dialog"
import { AccountingPeriodStatus, getAccountingPeriodStatus } from "../utils/accountingPeriod"

const expenseLineFormSchema = z.object({
  expenseAccountId: z.preprocess((val) => val ?? "", z.string().min(1, "Expense Account is required")),
  amount: z
    .number({ invalid_type_error: "Amount is required" })
    .nullable()
    // Explicit `: boolean` annotation defeats TS narrowing of `nullable()` output
    // (otherwise the inferred type collapses to `number`, breaking `amount: null` defaults).
    .refine((val): boolean => val !== null, "Amount is required")
    .refine((val) => val === null || val > 0, "Amount must be a positive number")
    .refine((val) => val === null || val <= INVOICE_TOTAL_MAX, `Amount must not exceed ${INVOICE_TOTAL_MAX}`), // AS400 constraint
})

// Shared with ExpenseLinesSection so the display message is computed the same way the
// schema validates it, without routing the value through react-hook-form's error state.
export const getExpenseLinesSumMismatchMessage = (
  totalAmount: number | null,
  expenseLines: { amount: number | null }[]
): string | undefined => {
  if (totalAmount === null || expenseLines.some((line) => line.amount === null)) return undefined

  const sum = expenseLines.reduce((acc, line) => acc + (line.amount ?? 0), 0)
  const diff = Math.abs(sum - totalAmount)
  if (diff <= INVOICE_AMOUNT_TOLERANCE) return undefined

  return `Expense line amounts must sum to Total Amount to Pay ($${totalAmount.toFixed(2)}). Current sum: $${sum.toFixed(2)}.`
}

const invoiceAttachmentFormSchema = z.object({
  file: z.instanceof(File, { message: "File is required" }),
  documentType: z.string().min(1, "Document Type is required"),
})

// Factory: comment becomes required when mode is anything other than "create". This is the
// canonical way to enforce the AK-5095 invariant "Comment is required for Edit / Cancel".
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createInvoiceFormSchema = (mode: InvoiceFormDialogMode) => {
  const isCommentRequired = mode !== "create"
  const commentSchema = isCommentRequired
    ? z.string().min(1, "Comment is required").max(500, "Comment must not exceed 500 characters") // AS400 constraint
    : z.string().max(500, "Comment must not exceed 500 characters").optional() // AS400 constraint

  // Per AK-5095 spec AC #12: only Comment is required for Edit — existing attachments stay
  // intact, so the form must NOT force a NEW attachment in edit modes.
  const attachmentsSchema = isCommentRequired
    ? z.array(invoiceAttachmentFormSchema)
    : z.array(invoiceAttachmentFormSchema).min(1, "At least one attachment is required")

  return z
    .object({
      vendorId: z.preprocess((val) => val ?? "", z.string().min(1, "Vendor Name is required")),
      invoiceNumber: z
        .string()
        .min(1, "Vendor Invoice # is required")
        .max(15, "Vendor Invoice # must not exceed 15 characters"), // AS400 constraint
      invoiceDate: z.preprocess((val) => val ?? "", z.string().min(1, "Invoice Date or Date Received is required")),
      dueDate: z.preprocess((val) => val ?? "", z.string().min(1, "Due Date is required")),
      totalAmount: z
        .number({ invalid_type_error: "Total Amount to Pay is required" })
        .nullable()
        // Explicit `: boolean` annotation defeats TS narrowing of `nullable()` output
        // (otherwise the inferred type collapses to `number`, breaking nullable defaults).
        .refine((val): boolean => val !== null, "Total Amount to Pay is required")
        .refine((val) => val === null || val > 0, "Total Amount to Pay must be a positive number")
        .refine(
          (val) => val === null || val <= INVOICE_TOTAL_MAX,
          `Total Amount to Pay must not exceed ${INVOICE_TOTAL_MAX}`
        ), // AS400 constraint
      printedCheck: z.boolean(),
      expenseLines: z.array(expenseLineFormSchema).min(1, "At least one expense account is required"),
      attachments: attachmentsSchema,
      comment: commentSchema,
    })
    .superRefine((values, ctx) => {
      // AK-6573 AC2: a future Accounting Period cannot be saved, which keeps Save disabled.
      if (getAccountingPeriodStatus(values.invoiceDate) === AccountingPeriodStatus.Future) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FUTURE_ACCOUNTING_PERIOD_ERROR,
          path: ["invoiceDate"],
        })
      }

      if (values.invoiceDate && values.dueDate && values.dueDate < values.invoiceDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Due Date must be on or after Invoice Date",
          path: ["dueDate"],
        })
      }

      // Cross-field invariant (AK-5504 fix #5 — AC scenario #2): the sum of per-line
      // amounts must match Total Amount to Pay. Anchored on `expenseLines` (root) so the
      // error surfaces under the section, not on any one row.
      const sumMismatchMessage = getExpenseLinesSumMismatchMessage(values.totalAmount, values.expenseLines)
      if (sumMismatchMessage) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: sumMismatchMessage,
          path: ["expenseLines"],
        })
      }
    })
}

// Default schema for backward compatibility with AK-5504/AK-5505 imports.
export const invoiceFormSchema = createInvoiceFormSchema("create")

export type InvoiceFormValues = z.infer<ReturnType<typeof createInvoiceFormSchema>>

export type InvoiceExpenseLineFormValues = z.infer<typeof expenseLineFormSchema>

export type InvoiceAttachmentFormValues = z.infer<typeof invoiceAttachmentFormSchema>
